import { useInputFocus } from "@moe/priv/hooks/use-input-focused";
import { useScaleToParent } from "@moe/priv/hooks/useScaleToParent";
import { cn } from "@moe/priv/utils";
import { useNavigate } from "@tanstack/react-router";
import { AvatarGroup } from "@web/components/AvatarGroup";
import { useHideOnScroll } from "@web/hooks/use-hide-on-scroll";
import { useDataContext } from "@web/route-services/chat/data-context";
import { useServiceContext } from "@web/route-services/chat/service-context";
import { ManageCharactersModal } from "@web/route-services/chat/settings/ManageCharactersModal";
import { SettingsButton } from "@web/route-services/chat/settings/SettingsButton";
import { getChatTitle } from "@web/route-services/chat/utils";
import { useAppContext } from "@web/route-services/root/AppContext";
import { ArrowLeftIcon } from "lucide-react";
import { motion } from "motion/react";
import { RefObject, useEffect, useRef, useState } from "react";

interface Props {
  className?: string;
  isHistoryPending: boolean;
}
/**
 * The chatbox's header bar
 */
export function Header({ isHistoryPending, className }: Props) {
  const { isDesktopScreen, createModal } = useAppContext();
  const { chatID } = useDataContext();
  const { chars } = useDataContext();
  const chatName = getChatTitle(Array.from(chars.values()).map((c) => c.name));
  const nav = useNavigate();
  const shouldHide = useHideOnScroll({ sensitivity: 1, yThreshold: 300 });
  const isInputFocused = useInputFocus();
  const headerRef = useRef<HTMLDivElement>(null);
  const { isGenerating } = useServiceContext();

  /**
   * On chat load, the chat history is scrolled to the bottom.
   * We don't want this scroll event to hide the header.
   * So we force the header to be visible for 1 second after the chat history is loaded.
   */
  const [forceVisible, setForceVisible] = useState(true);
  useEffect(() => {
    if (isHistoryPending) {
      setForceVisible(true);
    } else {
      setTimeout(() => {
        setForceVisible(false);
      }, 1000);
    }
  }, [isHistoryPending]);

  const isVisible =
    ((!shouldHide || isDesktopScreen) && (isDesktopScreen || !isInputFocused)) || forceVisible || isGenerating;

  const avatarList = Array.from(chars.values()).map((char) => ({
    id: char.id,
    url: char.avatar
  }));

  const handleClick = () => {
    createModal({
      content: <ManageCharactersModal chatID={chatID} />,
      headerDragOnly: true
    });
  };

  useScaleToParent(headerRef as RefObject<HTMLElement>, {
    enabled: isDesktopScreen,
    offsetPx: 10
  });

  return (
    <motion.div
      ref={headerRef}
      className={cn(`fixed left-0 top-0 z-10 flex w-full flex-col md:absolute`, className)}
      initial={false}
      animate={{
        transform: isVisible ? "translateY(0)" : "translateY(-100%)",
        opacity: isVisible ? 1 : 0
      }}
      transition={{
        transform: { duration: isVisible ? 0.22 : 0.2, ease: "easeInOut" },
        opacity: { duration: isVisible ? 0.1 : 0.2, ease: "easeIn" }
      }}
    >
      <div className="border-line h-15 bg-background md:bg-chat-header-background flex w-full items-center gap-3 border-b pb-1 pl-2 pr-4 pt-1 md:rounded-b-xl md:pb-2.5 md:pl-6 md:pt-2.5">
        {!isDesktopScreen && (
          <button
            className="px-3 py-2"
            onClick={() => {
              nav({ to: "/recent-chats" });
            }}
          >
            <ArrowLeftIcon
              strokeWidth={3.2}
              className="text-tx-tertiary hover:text-tx-primary active:text-tx-primary size-6 transition duration-300 ease-out"
            />
          </button>
        )}
        <AvatarGroup avatars={avatarList} onGroupClick={handleClick} maxWidth="14rem" avatarClassName="w-9 h-9" />
        <p className="text-md text-tx-tertiary line-clamp-1 max-w-96 text-ellipsis font-bold">{chatName}</p>
        <SettingsButton className="ml-auto" />
      </div>
    </motion.div>
  );
}
